/**
 * What can be wrong with a drawing before anyone tries to convert it.
 *
 * Garbage in, garbage out is not a disclaimer here, it is a design position:
 * the settings a producer exported with decide the accuracy that is reachable,
 * and nothing downstream recovers text that was written as outlines. So the
 * file is inspected first and each defect is **named** — what was measured, why
 * a producer does it, and what to change and export again.
 *
 * The set is closed, like the error codes and the quality-issue taxonomy, for
 * the same reason: a defect that can be described in free text is a defect
 * nobody can count, gate or write guidance for.
 */

export type DefectKind =
  /** The file could not be opened at all. */
  | 'encrypted'
  | 'not_a_pdf'
  | 'corrupt'
  | 'too_large'
  | 'timed_out'
  /** The file opened and carries nothing this pipeline can convert. */
  | 'raster_only'
  | 'no_content'
  /** The file is convertible, and something in it will be lost or is at risk. */
  | 'raster_content'
  | 'text_as_outlines'
  | 'text_layer_over_scan'
  | 'unsupported_curves'
  | 'font_not_embedded'
  | 'non_standard_sheet'
  | 'content_outside_page'
  | 'text_write_order';

/**
 * How much a defect costs.
 *
 * `blocking` means no DXF at all — either the file did not open, or it carries
 * nothing to convert. `degrading` means a DXF will be produced and something
 * named will be missing from it. `advisory` means the conversion is sound and
 * the reader should know a risk exists anyway.
 *
 * The distinction is what stops the surface crying wolf: a logo on an otherwise
 * vector sheet is not a reason to refuse a drawing, and treating it as one
 * rejects work that would have converted perfectly.
 */
export type DefectSeverity = 'blocking' | 'degrading' | 'advisory';

export const DEFECT_SEVERITY: Record<DefectKind, DefectSeverity> = {
  encrypted: 'blocking',
  not_a_pdf: 'blocking',
  corrupt: 'blocking',
  too_large: 'blocking',
  timed_out: 'blocking',
  raster_only: 'blocking',
  no_content: 'blocking',

  text_as_outlines: 'degrading',
  raster_content: 'degrading',
  text_layer_over_scan: 'degrading',
  unsupported_curves: 'degrading',
  content_outside_page: 'degrading',

  font_not_embedded: 'advisory',
  non_standard_sheet: 'advisory',
  text_write_order: 'advisory',
};

/**
 * Whether exporting the drawing again, with different settings, would change
 * this outcome.
 *
 * False for the defects that are properties of the *drawing* rather than of the
 * export — a genuine spline is a spline however it is written out, and a sheet
 * that is not A-series was authored that way. Inviting somebody to re-export
 * for those wastes their afternoon, which is why it is recorded per defect
 * rather than assumed from the severity.
 */
export const RE_EXPORT_HELPS: Record<DefectKind, boolean> = {
  encrypted: true,
  not_a_pdf: true,
  corrupt: true,
  too_large: false,
  timed_out: false,
  raster_only: true,
  no_content: true,

  text_as_outlines: true,
  raster_content: false,
  text_layer_over_scan: true,
  unsupported_curves: false,
  content_outside_page: true,

  font_not_embedded: true,
  non_standard_sheet: false,
  text_write_order: false,
};

export interface Defect {
  kind: DefectKind;
  severity: DefectSeverity;
  /** How many instances. One for a page-level or file-level fact. */
  count: number;
  /** Absent for a fact about the whole file. */
  pageIndex?: number;
  /**
   * What was measured, in numbers, and nothing else.
   *
   * Never a judgement and never advice: the cause and the recommendation are
   * the same for every instance of a kind and live in the guidance catalogue,
   * so that one sentence serves the screen, the report and the export-settings
   * document AIONA distributes. This field is the part that differs per file.
   */
  measured: string;
  bbox?: readonly [number, number, number, number];
}

const RANK: Record<DefectSeverity, number> = { blocking: 0, degrading: 1, advisory: 2 };

/** Worst first, so the reason a drawing was refused is read before the details. */
export function bySeverity(a: Defect, b: Defect): number {
  return RANK[a.severity] - RANK[b.severity];
}
